import Autocomplete from "@mui/material/Autocomplete"
import FormControl from "@mui/material/FormControl"
import FormHelperText from "@mui/material/FormHelperText"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"

import Column from "../../../common/components/Column"
import {
  BUSINESS_TYPES,
  EIN_HELP,
  LEGAL_NAME_HELP,
  LEGAL_NAME_LABEL,
  collapseWhitespace,
  normalizeEin,
} from "./a2pValidation"

export const BUSINESS_INDUSTRIES = [
  "AGRICULTURE",
  "AUTOMOTIVE",
  "BANKING",
  "CONSTRUCTION",
  "CONSUMER",
  "EDUCATION",
  "ELECTRONICS",
  "ENGINEERING",
  "ENERGY",
  "FAST_MOVING_CONSUMER_GOODS",
  "FINANCIAL",
  "FINTECH",
  "FOOD_AND_BEVERAGE",
  "GOVERNMENT",
  "HEALTHCARE",
  "HOSPITALITY",
  "INSURANCE",
  "JEWELRY",
  "LEGAL",
  "MANUFACTURING",
  "MEDIA",
  "NOT_FOR_PROFIT",
  "OIL_AND_GAS",
  "ONLINE",
  "PROFESSIONAL_SERVICES",
  "RAW_MATERIALS",
  "REAL_ESTATE",
  "RELIGION",
  "RETAIL",
  "TECHNOLOGY",
  "TELECOMMUNICATIONS",
  "TRANSPORTATION",
  "TRAVEL",
].map((industry) => ({
  id: industry,
  label: `${industry[0]}${industry
    .substring(1)
    .toLowerCase()
    .replaceAll("_", " ")}`,
}))

export const EMPTY_BUSINESS = {
  business_name: "",
  website_url: "",
  social_media_profile_urls: "",
  business_type: "",
  business_industry: "REAL_ESTATE",
  business_registration_identifier: "EIN",
  business_registration_number: "",
}

/** Field-level errors for the business form, or {} when it can be submitted. */
export function businessErrors(business) {
  const errors = {}
  if (!collapseWhitespace(business.business_name)) {
    errors.business_name = "Required"
  }
  if (!business.website_url) {
    errors.website_url = "Required"
  } else if (!isWebUrl(business.website_url)) {
    errors.website_url = "Enter a valid website address"
  }
  if (
    business.social_media_profile_urls &&
    !splitUrls(business.social_media_profile_urls).every(isWebUrl)
  ) {
    errors.social_media_profile_urls = "Enter valid URLs, separated by commas"
  }
  if (!business.business_type) {
    errors.business_type = "Required"
  }
  if (!business.business_industry) {
    errors.business_industry = "Required"
  }
  if (business.business_registration_identifier === "EIN") {
    const { error } = normalizeEin(business.business_registration_number)
    if (error) {
      errors.business_registration_number = error
    }
  } else if (
    String(business.business_registration_number || "").replace(/[^0-9]/g, "")
      .length < 9
  ) {
    errors.business_registration_number =
      "Enter the first 9 digits of your Canadian Business Number"
  }
  return errors
}

/** Cleaned payload for the API (backend normalizes again). */
export function businessPayload(business) {
  const ein =
    business.business_registration_identifier === "EIN"
      ? normalizeEin(business.business_registration_number).value
      : business.business_registration_number
  return {
    business_name: collapseWhitespace(business.business_name),
    website_url: withScheme(business.website_url.trim()),
    social_media_profile_urls: business.social_media_profile_urls
      ? splitUrls(business.social_media_profile_urls).map(withScheme).join(", ")
      : "",
    business_type: business.business_type,
    business_industry: business.business_industry,
    business_registration_identifier: business.business_registration_identifier,
    business_registration_number: ein,
  }
}

function withScheme(url) {
  const value = String(url || "").trim()
  if (!value) {
    return value
  }
  return /^https?:\/\//i.test(value) ? value : `https://${value}`
}

export function BusinessInfoFields({ value, onChange, highlight = [] }) {
  const errors = businessErrors(value)
  const touched = (field) =>
    value[field] !== "" && value[field] !== null && value[field] !== undefined
  const isHighlighted = (field) => highlight.includes(field)
  const set = (field) => (e) => onChange({ ...value, [field]: e.target.value })
  const industry =
    BUSINESS_INDUSTRIES.find(
      (option) => option.id === value.business_industry
    ) || null

  return (
    <Column sx={{ gap: 3 }}>
      <TextField
        label={LEGAL_NAME_LABEL}
        value={value.business_name}
        onChange={set("business_name")}
        onBlur={() =>
          onChange({
            ...value,
            business_name: collapseWhitespace(value.business_name),
          })
        }
        helperText={LEGAL_NAME_HELP}
        error={isHighlighted("business_name")}
        focused={isHighlighted("business_name") || undefined}
        required
        fullWidth
      />
      <FormControl fullWidth size="small" required>
        <InputLabel id="a2p-tax-id-type">Tax ID type</InputLabel>
        <Select
          labelId="a2p-tax-id-type"
          label="Tax ID type"
          value={value.business_registration_identifier}
          onChange={set("business_registration_identifier")}
        >
          <MenuItem value="EIN">US EIN</MenuItem>
          <MenuItem value="CBN">Canadian Business Number</MenuItem>
        </Select>
      </FormControl>
      <TextField
        label={
          value.business_registration_identifier === "EIN"
            ? "EIN (9 digits)"
            : "Canadian Business Number"
        }
        value={value.business_registration_number}
        onChange={set("business_registration_number")}
        onBlur={() => {
          if (value.business_registration_identifier !== "EIN") {
            return
          }
          const { value: normalized, error } = normalizeEin(
            value.business_registration_number
          )
          if (!error) {
            onChange({ ...value, business_registration_number: normalized })
          }
        }}
        placeholder={
          value.business_registration_identifier === "EIN"
            ? "12-3456789"
            : "First 9 digits"
        }
        error={
          (touched("business_registration_number") &&
            !!errors.business_registration_number) ||
          isHighlighted("business_registration_number")
        }
        helperText={
          (touched("business_registration_number") &&
            errors.business_registration_number) ||
          (value.business_registration_identifier === "EIN" ? EIN_HELP : "")
        }
        inputProps={{ inputMode: "numeric", autoComplete: "off" }}
        required
        fullWidth
      />
      <FormControl
        fullWidth
        size="small"
        required
        error={isHighlighted("business_type")}
      >
        <InputLabel id="a2p-business-type">Business type</InputLabel>
        <Select
          labelId="a2p-business-type"
          label="Business type"
          value={value.business_type}
          onChange={set("business_type")}
        >
          {BUSINESS_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          As registered with the IRS / your state (an LLC is "Limited Liability
          Corporation").
        </FormHelperText>
      </FormControl>
      <Autocomplete
        value={industry}
        onChange={(e, option) =>
          onChange({ ...value, business_industry: option?.id || "" })
        }
        options={BUSINESS_INDUSTRIES}
        renderInput={(props) => (
          <TextField label="Business industry" required {...props} />
        )}
        fullWidth
        size="small"
      />
      <TextField
        label="Business website"
        value={value.website_url}
        onChange={set("website_url")}
        error={
          (touched("website_url") && !!errors.website_url) ||
          isHighlighted("website_url")
        }
        helperText={
          (touched("website_url") && errors.website_url) ||
          "A working site that shows this business's name (your agent page is fine)."
        }
        required
        fullWidth
      />
      <TextField
        label="Social media profile URL (optional)"
        value={value.social_media_profile_urls}
        onChange={set("social_media_profile_urls")}
        error={!!errors.social_media_profile_urls}
        helperText={errors.social_media_profile_urls}
        fullWidth
      />
    </Column>
  )
}

export const EMPTY_SOLE_PROP = {
  first_name: "",
  last_name: "",
  email: "",
  mobile_phone_number: "",
  brand_name: "",
  street: "",
  street_secondary: "",
  city: "",
  region: "",
  postal_code: "",
  iso_country: "US",
}

export function solePropErrors(data) {
  const errors = {}
  ;["first_name", "last_name", "email", "street", "city", "region"].forEach(
    (field) => {
      if (!collapseWhitespace(data[field])) {
        errors[field] = "Required"
      }
    }
  )
  if (!collapseWhitespace(data.postal_code)) {
    errors.postal_code = "Required"
  }
  const digits = String(data.mobile_phone_number || "").replace(/[^0-9]/g, "")
  const national =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits
  if (national.length !== 10) {
    errors.mobile_phone_number = "Enter a 10-digit US or Canadian mobile number"
  }
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
    errors.email = "Enter a valid email"
  }
  return errors
}

export function solePropPayload(data) {
  const payload = {}
  Object.entries(data).forEach(([key, val]) => {
    payload[key] = typeof val === "string" ? collapseWhitespace(val) : val
  })
  return payload
}

export function SolePropFields({ value, onChange, highlight = [] }) {
  const errors = solePropErrors(value)
  const set = (field) => (e) => onChange({ ...value, [field]: e.target.value })
  const shown = (field) =>
    (value[field] && errors[field]) || highlight.includes(field)

  return (
    <Column sx={{ gap: 3 }}>
      <TextField
        label="First name"
        value={value.first_name}
        onChange={set("first_name")}
        required
        fullWidth
      />
      <TextField
        label="Last name"
        value={value.last_name}
        onChange={set("last_name")}
        required
        fullWidth
      />
      <TextField
        label="Email"
        type="email"
        value={value.email}
        onChange={set("email")}
        error={!!shown("email")}
        helperText={(value.email && errors.email) || ""}
        required
        fullWidth
      />
      <TextField
        label="Your mobile phone number"
        type="tel"
        value={value.mobile_phone_number}
        onChange={set("mobile_phone_number")}
        error={!!shown("mobile_phone_number")}
        helperText={
          (value.mobile_phone_number && errors.mobile_phone_number) ||
          "We'll text this number a confirmation request — you'll need to reply YES. Use your real cell phone (not a Google Voice, VoIP or Twilio number). A mobile number can only be used for 3 registrations."
        }
        required
        fullWidth
      />
      <TextField
        label="Brand name (optional)"
        value={value.brand_name}
        onChange={set("brand_name")}
        helperText="Usually your full name, or the name you do business as. Defaults to your name."
        fullWidth
      />
      <TextField
        label="Street address"
        value={value.street}
        onChange={set("street")}
        error={highlight.includes("street")}
        required
        fullWidth
      />
      <TextField
        label="Apartment, suite, etc. (optional)"
        value={value.street_secondary}
        onChange={set("street_secondary")}
        fullWidth
      />
      <TextField
        label="City"
        value={value.city}
        onChange={set("city")}
        required
        fullWidth
      />
      <TextField
        label="State / province (2 letters)"
        value={value.region}
        onChange={set("region")}
        placeholder="TX"
        required
        fullWidth
      />
      <TextField
        label="ZIP / postal code"
        value={value.postal_code}
        onChange={set("postal_code")}
        required
        fullWidth
      />
      <FormControl fullWidth size="small" required>
        <InputLabel id="a2p-sp-country">Country</InputLabel>
        <Select
          labelId="a2p-sp-country"
          label="Country"
          value={value.iso_country}
          onChange={set("iso_country")}
        >
          <MenuItem value="US">United States of America</MenuItem>
          <MenuItem value="CA">Canada</MenuItem>
        </Select>
      </FormControl>
    </Column>
  )
}

/** Same rule as the backend: http(s) URL whose host has a dot, no spaces. */
export function isWebUrl(value) {
  const url = withScheme(value)
  if (!url || /\s/.test(url)) {
    return false
  }
  try {
    return new URL(url).hostname.includes(".")
  } catch {
    return false
  }
}

function splitUrls(value) {
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
}
